import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ScheduleCalendar from "../../components/ScheduleCalendar/ScheduleCalendar";
import SaturnePage from "../../components/SaturnePage/SaturnePage";
import { getErrorMessage } from "../../services/ErrorMessage";
import { useScheduleCalendar } from "../../hooks/useScheduleCalendar";
import UserService from "../../services/UserService";
import { ScheduleSummary } from "../../services/scheduling/Scheduling.types";
import { createSchedulingServices } from "../../services/scheduling/SchedulingServiceFactory";

export default function MySchedulesPage() {
  const { t } = useTranslation();
  const services = useMemo(createSchedulingServices, []);
  const [schedules, setSchedules] = useState<ScheduleSummary[]>([]);
  const [schedulesLoading, setSchedulesLoading] = useState(true);
  const [schedulesError, setSchedulesError] = useState<string>();
  const email = UserService.getEmail();
  const calendar = useScheduleCalendar(services.calendarService, schedules);

  const loadSchedules = useCallback(async () => {
    setSchedulesLoading(true);
    setSchedulesError(undefined);

    if (!email) {
      setSchedules([]);
      setSchedulesError(t("mySchedules.missingEmail"));
      setSchedulesLoading(false);
      return;
    }

    try {
      const actorCalendar = await services.userSchedulesService.getForEmail(email);
      setSchedules(actorCalendar.schedules);
    } catch (error) {
      setSchedules([]);
      setSchedulesError(getErrorMessage(error));
    } finally {
      setSchedulesLoading(false);
    }
  }, [email, services.userSchedulesService, t]);

  useEffect(() => {
    void loadSchedules();
  }, [loadSchedules]);

  return (
    <SaturnePage loading={false} needsLogin wide>
      <ScheduleCalendar
        title={t("mySchedules.title")}
        subtitle={
          email
            ? t("mySchedules.subtitle", { email })
            : t("mySchedules.subtitleWithoutEmail")
        }
        schedules={schedules}
        selectedScheduleIds={calendar.selectedScheduleIds}
        events={calendar.events}
        anchorDate={calendar.anchorDate}
        view={calendar.view}
        schedulesLoading={schedulesLoading}
        eventsLoading={calendar.eventsLoading}
        schedulesError={schedulesError}
        eventsError={calendar.eventsError}
        onSelectedScheduleIdsChange={calendar.setSelectedScheduleIds}
        onAnchorDateChange={calendar.setAnchorDate}
        onViewChange={calendar.setView}
        onRetrySchedules={() => void loadSchedules()}
        onRetryEvents={calendar.retryEvents}
      />
    </SaturnePage>
  );
}
